import { combineReducers } from 'redux';
import { produce } from 'immer';

import {
    GET_USERS_SUCCESS,
    DELETE_USER,
    DELETE_MANY_USERS,
    DEACTIVATE_USER,
    SAVE_USER,
    UPDATE_USER,
    ACTIVATE_USER,
    GET_USER_DETAIL_SUCCESS,
    GET_USER_DETAIL_FAIL,
    GET_USER_DETAIL,
} from '../../constants/actions/users.constants';
import { IAction, IUser } from '../../types';
import { LOGOUT } from '../../constants/actions/auth.constants';

const detail = combineReducers({ data: detailData, loading: detailLoading });

const reducer = combineReducers({ list, detail });

export default reducer;

export type UsersState = ReturnType<typeof reducer>;

function detailData(state: IUser | null = null, action) {
    const { payload, type } = action;

    switch (type) {
        case GET_USER_DETAIL_SUCCESS:
            return payload;
        case LOGOUT:
            return null;
        default:
            return state;
    }
}

function detailLoading(state: boolean = false, action) {
    switch (action.type) {
        case GET_USER_DETAIL:
            return true;
        case GET_USER_DETAIL_SUCCESS:
        case GET_USER_DETAIL_FAIL:
            return false;
        default:
            return state;
    }
}

function list(state: IUser[] = [], { payload, type }: IAction) {
    switch (type) {
        case GET_USERS_SUCCESS:
            return getUsers(payload);
        case DELETE_USER:
            return deleteUser(payload);
        case DELETE_MANY_USERS:
            return deleteManyUsers(payload);
        case DEACTIVATE_USER:
            return deactivateUser(payload);
        case ACTIVATE_USER:
            return activateUser(payload);
        case SAVE_USER:
            return saveUser(payload);
        case UPDATE_USER:
            return updateUser(payload.id, payload.user);
        case LOGOUT:
            return [];
        default:
            return state;
    }

    function getUsers(users: IUser[]) {
        return users;
    }

    function deleteUser(id: number) {
        return produce(state, (draft) => {
            const index = state.findIndex((user: IUser) => id === user.userId);
            if (index >= 0) {
                draft.splice(index, 1);
            }
        });
    }

    function activateUser(id: number) {
        return changeActiveState(id, true);
    }

    function deactivateUser(id: number) {
        return changeActiveState(id, false);
    }

    function changeActiveState(id: number, isActive: boolean) {
        const index = state.findIndex((user: IUser) => id === user.userId);
        if (index >= 0) {
            return produce(state, (draft) => {
                draft[index].isActive = isActive;
            });
        }

        return state;
    }

    function saveUser(user: IUser) {
        return produce(state, (draft) => {
            draft.push(user);
        });
    }

    function updateUser(id: number, user: IUser) {
        const index = state.findIndex((user: IUser) => id === user.userId);
        if (index >= 0) {
            return produce(state, (draft) => {
                draft[index] = user;
            });
        }
        return state;
    }

    function deleteManyUsers(ids: number[]) {
        return state.filter((user: IUser) => !ids.includes(user.userId));
    }
}
